import React from 'react';
import PropTypes from 'prop-types';
import {Accordion, AccordionItem} from '@/components/molecules';
import {
  TextArea,
  ButtonWithIcon,
  Divider,
  LanguageTabs
} from '@/components/atoms';
import FreeKickGuidelineModal from './FreeKickGuidelineModal';
import TeamSection from './TeamSection';
import {buildImageHandlersFromProps} from '../shared/buildImageHandlersFromProps';
import GameDisplayAccordionItem from '../shared/GameDisplayAccordionItem';
import ImageUploadAccordionItem from '../shared/ImageUploadAccordionItem';
import {
  FREEKICK_BACKGROUND_IMAGE_FIELDS,
  FREEKICK_BACKGROUND_SECTION_CONFIG
} from './freekickImageFieldConfig';
import {
  FREEKICK_ADDITIONAL_IMAGE_FIELDS,
  FREEKICK_ADDITIONAL_SECTION_CONFIG
} from './freekickAdditionalImageFieldConfig';
import styles from './FreeKickGameDisplay.module.scss';
import '../shared/ActivityDisplayAccordion.scss';

const FreeKickGameDisplayForm = ({
  languages,
  activeTabIndex,
  setActiveTabIndex,
  tutorialTabIndex,
  setTutorialTabIndex,
  currentLangData,
  tutorialLangData,
  updateLangField,
  updateTutorialField,
  gameData,
  updateGameField,
  handleGameImageUpload,
  handleGameImageDelete,
  handleLogoImageUpload,
  handleLogoImageDelete,
  handleFirstTeamImageUpload,
  handleFirstTeamImageDelete,
  handleSecondTeamImageUpload,
  handleSecondTeamImageDelete,
  handleBackgroundMenuUpload,
  handleBackgroundMenuDelete,
  handleBackgroundSelectTeamUpload,
  handleBackgroundSelectTeamDelete,
  handleBackgroundImageUpload,
  handleBackgroundImageDelete,
  handleBackgroundNextLevelUpload,
  handleBackgroundNextLevelDelete,
  handleMissImageUpload,
  handleMissImageDelete,
  handleOutImageUpload,
  handleOutImageDelete,
  handleGameOverImageUpload,
  handleGameOverImageDelete,
  handleGoalImageUpload,
  handleGoalImageDelete,
  handleHighBarImageUpload,
  handleHighBarImageDelete,
  handleRightBarImageUpload,
  handleRightBarImageDelete,
  gameImageProgress,
  logoImageProgress,
  firstTeamImageProgress,
  secondTeamImageProgress,
  backgroundMenuProgress,
  backgroundSelectTeamProgress,
  backgroundImageProgress,
  backgroundNextLevelProgress,
  missImageProgress,
  outImageProgress,
  gameOverImageProgress,
  goalImageProgress,
  highBarImageProgress,
  rightBarImageProgress,
  gameImageValidationError = '',
  logoImageValidationError = '',
  teamImageValidationError = '',
  secondTeamImageValidationError = '',
  backgroundMenuValidationError = '',
  backgroundSelectTeamValidationError = '',
  backgroundImageValidationError = '',
  backgroundNextLevelValidationError = '',
  missImageValidationError = '',
  outImageValidationError = '',
  gameOverImageValidationError = '',
  goalImageValidationError = '',
  highBarImageValidationError = '',
  rightBarImageValidationError = '',
  handleGameImageFileError,
  handleLogoImageFileError,
  handleFirstTeamImageFileError,
  handleSecondTeamImageFileError,
  handleBackgroundMenuFileError,
  handleBackgroundSelectTeamFileError,
  handleBackgroundImageFileError,
  handleBackgroundNextLevelFileError,
  handleMissImageFileError,
  handleOutImageFileError,
  handleGameOverImageFileError,
  handleGoalImageFileError,
  handleHighBarImageFileError,
  handleRightBarImageFileError,
  showGuidelines,
  setShowGuidelines
}) => {
  const backgroundImageHandlers = buildImageHandlersFromProps(
    {
      handleBackgroundMenuUpload,
      handleBackgroundMenuDelete,
      handleBackgroundMenuFileError,
      backgroundMenuProgress,
      backgroundMenuValidationError,
      handleBackgroundSelectTeamUpload,
      handleBackgroundSelectTeamDelete,
      handleBackgroundSelectTeamFileError,
      backgroundSelectTeamProgress,
      backgroundSelectTeamValidationError,
      handleBackgroundImageUpload,
      handleBackgroundImageDelete,
      handleBackgroundImageFileError,
      backgroundImageProgress,
      backgroundImageValidationError,
      handleBackgroundNextLevelUpload,
      handleBackgroundNextLevelDelete,
      handleBackgroundNextLevelFileError,
      backgroundNextLevelProgress,
      backgroundNextLevelValidationError
    },
    FREEKICK_BACKGROUND_IMAGE_FIELDS
  );

  const additionalImageHandlers = buildImageHandlersFromProps(
    {
      handleMissImageUpload,
      handleMissImageDelete,
      handleMissImageFileError,
      missImageProgress,
      missImageValidationError,
      handleOutImageUpload,
      handleOutImageDelete,
      handleOutImageFileError,
      outImageProgress,
      outImageValidationError,
      handleGameOverImageUpload,
      handleGameOverImageDelete,
      handleGameOverImageFileError,
      gameOverImageProgress,
      gameOverImageValidationError,
      handleGoalImageUpload,
      handleGoalImageDelete,
      handleGoalImageFileError,
      goalImageProgress,
      goalImageValidationError,
      handleHighBarImageUpload,
      handleHighBarImageDelete,
      handleHighBarImageFileError,
      highBarImageProgress,
      highBarImageValidationError,
      handleRightBarImageUpload,
      handleRightBarImageDelete,
      handleRightBarImageFileError,
      rightBarImageProgress,
      rightBarImageValidationError
    },
    FREEKICK_ADDITIONAL_IMAGE_FIELDS
  );

  return (
    <div
      className='freekick-display-form mb-8 flex flex-col gap-6'
      data-testid='freekick-display-form'>
      <Accordion
        isDark
        size='large'
        content={
          <div className='w-full space-y-5'>
            <GameDisplayAccordionItem
              index={0}
              metadataProps={{
                languages,
                activeTabIndex,
                setActiveTabIndex,
                currentLangData,
                updateLangField,
                gameData,
                handleGameImageUpload,
                handleGameImageDelete,
                handleLogoImageUpload,
                handleLogoImageDelete,
                gameImageProgress,
                logoImageProgress,
                gameImageValidationError,
                logoImageValidationError,
                handleGameImageFileError,
                handleLogoImageFileError,
                setShowGuidelines
              }}
            />
            <AccordionItem
              isDark
              expanded
              index={1}
              label='Game Settings'
              onUpdate={() => {}}
              subTitle='Team selection and tutorial overview'
              content={
                <div className='w-full'>
                  <div
                    className='rounded-lg shadow-[0_0_24px_rgba(0,0,0,0.08)]'
                    style={{
                      backgroundColor: 'var(--color-background-layer-01)'
                    }}
                    data-testid='game-settings-section'>
                    <div className='flex flex-col gap-4 px-4 py-3'>
                      <div className='flex items-center gap-4'>
                        {/* TODO: Restore Download Sample Image when sample download is implemented
                        <ButtonWithIcon
                          id='download-sample-image-settings'
                          handleButton={() => {}}
                          uiSchema={{
                            title: 'Download Sample Ima...',
                            variant: 'tertiary',
                            size: 'small',
                            isDark: true,
                            iconTrailing: 'download'
                          }}
                        />
                        */}
                        <ButtonWithIcon
                          id='view-guideline-settings'
                          iconLeading='open_in_full'
                          handleButton={() => setShowGuidelines(true)}
                          uiSchema={{
                            title: 'View Guideline',
                            variant: 'tertiary',
                            size: 'small',
                            isDark: true
                          }}
                        />
                      </div>

                      <TeamSection
                        languages={languages}
                        tutorialTabIndex={tutorialTabIndex}
                        setTutorialTabIndex={setTutorialTabIndex}
                        tutorialLangData={tutorialLangData}
                        updateTutorialField={updateTutorialField}
                        gameData={gameData}
                        updateGameField={updateGameField}
                        handleFirstTeamImageUpload={handleFirstTeamImageUpload}
                        handleFirstTeamImageDelete={handleFirstTeamImageDelete}
                        handleSecondTeamImageUpload={
                          handleSecondTeamImageUpload
                        }
                        handleSecondTeamImageDelete={
                          handleSecondTeamImageDelete
                        }
                        firstTeamImageProgress={firstTeamImageProgress}
                        secondTeamImageProgress={secondTeamImageProgress}
                        imageValidationError={teamImageValidationError}
                        secondImageValidationError={
                          secondTeamImageValidationError
                        }
                        handleFirstTeamImageFileError={
                          handleFirstTeamImageFileError
                        }
                        handleSecondTeamImageFileError={
                          handleSecondTeamImageFileError
                        }
                        handleBackgroundMenuUpload={handleBackgroundMenuUpload}
                        handleBackgroundMenuDelete={handleBackgroundMenuDelete}
                        backgroundMenuProgress={backgroundMenuProgress}
                        backgroundMenuValidationError={
                          backgroundMenuValidationError
                        }
                        handleBackgroundMenuFileError={
                          handleBackgroundMenuFileError
                        }
                        backgroundImageHandlers={backgroundImageHandlers}
                        backgroundImageValidationError={
                          backgroundImageValidationError
                        }
                        backgroundNextLevelValidationError={
                          backgroundNextLevelValidationError
                        }
                        handleBackgroundSelectTeamUpload={
                          handleBackgroundSelectTeamUpload
                        }
                        handleBackgroundSelectTeamDelete={
                          handleBackgroundSelectTeamDelete
                        }
                        handleBackgroundSelectTeamFileError={
                          handleBackgroundSelectTeamFileError
                        }
                        backgroundSelectTeamProgress={
                          backgroundSelectTeamProgress
                        }
                        backgroundSelectTeamValidationError={
                          backgroundSelectTeamValidationError
                        }
                        setShowGuidelines={setShowGuidelines}
                      />

                      <Divider className='my-0 border-0 border-b' />

                      <div className='flex flex-col gap-4'>
                        <h4 className='text-[16px] font-bold leading-[22px] text-[#FFFFFF]'>
                          Tutorial
                        </h4>

                        <LanguageTabs
                          languages={languages}
                          activeTabIndex={tutorialTabIndex}
                          setActiveTabIndex={(index) =>
                            setTutorialTabIndex(index)
                          }
                          testIdPrefix='tutorial-language-tab'
                          dividerClassName='-mt-4 mb-0 border-0 border-b'
                        />

                        <div className='flex items-start gap-4'>
                          <div
                            className={`max-w-[380px] flex-1 ${styles.tutorialTextAreaWrapper}`}>
                            <TextArea
                              value={tutorialLangData.tutorialLine1}
                              updateValue={(val) =>
                                updateTutorialField('tutorialLine1', val)
                              }
                              label='Description - Line 1'
                              uiSchema={{
                                isDark: true,
                                placeholder: 'Description - Line 1',
                                maxLength: 500,
                                showToolbar: true,
                                showCharacterCount: true
                              }}
                              id='tutorial-line-1'
                              trim={false}
                            />
                          </div>
                          <div
                            className={`max-w-[380px] flex-1 ${styles.tutorialTextAreaWrapper}`}>
                            <TextArea
                              value={tutorialLangData.tutorialLine2}
                              updateValue={(val) =>
                                updateTutorialField('tutorialLine2', val)
                              }
                              label='Description - Line 2'
                              uiSchema={{
                                isDark: true,
                                placeholder: 'Description - Line 2',
                                maxLength: 500,
                                showToolbar: true,
                                showCharacterCount: true
                              }}
                              id='tutorial-line-2'
                              trim={false}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
            <ImageUploadAccordionItem
              index={2}
              label='Background Images'
              subTitle='Background images displayed within the game.'
              sectionConfig={FREEKICK_BACKGROUND_SECTION_CONFIG}
              gameData={gameData}
              imageFields={FREEKICK_BACKGROUND_IMAGE_FIELDS}
              imageHandlers={backgroundImageHandlers}
              setShowGuidelines={setShowGuidelines}
            />
            <ImageUploadAccordionItem
              index={3}
              label='Additional Images'
              subTitle='Visuals displayed across the game.'
              sectionConfig={FREEKICK_ADDITIONAL_SECTION_CONFIG}
              gameData={gameData}
              imageFields={FREEKICK_ADDITIONAL_IMAGE_FIELDS}
              imageHandlers={additionalImageHandlers}
              setShowGuidelines={setShowGuidelines}
            />
          </div>
        }
      />

      <FreeKickGuidelineModal
        showGuidelines={showGuidelines}
        setShowGuidelines={setShowGuidelines}
      />
    </div>
  );
};

FreeKickGameDisplayForm.propTypes = {
  languages: PropTypes.array.isRequired,
  activeTabIndex: PropTypes.number.isRequired,
  setActiveTabIndex: PropTypes.func.isRequired,
  tutorialTabIndex: PropTypes.number.isRequired,
  setTutorialTabIndex: PropTypes.func.isRequired,
  currentLangData: PropTypes.object.isRequired,
  tutorialLangData: PropTypes.object.isRequired,
  updateLangField: PropTypes.func.isRequired,
  updateTutorialField: PropTypes.func.isRequired,
  gameData: PropTypes.object.isRequired,
  updateGameField: PropTypes.func.isRequired,
  handleGameImageUpload: PropTypes.func.isRequired,
  handleGameImageDelete: PropTypes.func.isRequired,
  handleLogoImageUpload: PropTypes.func.isRequired,
  handleLogoImageDelete: PropTypes.func.isRequired,
  handleFirstTeamImageUpload: PropTypes.func.isRequired,
  handleFirstTeamImageDelete: PropTypes.func.isRequired,
  handleSecondTeamImageUpload: PropTypes.func.isRequired,
  handleSecondTeamImageDelete: PropTypes.func.isRequired,
  handleBackgroundMenuUpload: PropTypes.func.isRequired,
  handleBackgroundMenuDelete: PropTypes.func.isRequired,
  handleBackgroundSelectTeamUpload: PropTypes.func.isRequired,
  handleBackgroundSelectTeamDelete: PropTypes.func.isRequired,
  handleBackgroundImageUpload: PropTypes.func.isRequired,
  handleBackgroundImageDelete: PropTypes.func.isRequired,
  handleBackgroundNextLevelUpload: PropTypes.func.isRequired,
  handleBackgroundNextLevelDelete: PropTypes.func.isRequired,
  handleMissImageUpload: PropTypes.func.isRequired,
  handleMissImageDelete: PropTypes.func.isRequired,
  handleOutImageUpload: PropTypes.func.isRequired,
  handleOutImageDelete: PropTypes.func.isRequired,
  handleGameOverImageUpload: PropTypes.func.isRequired,
  handleGameOverImageDelete: PropTypes.func.isRequired,
  handleGoalImageUpload: PropTypes.func.isRequired,
  handleGoalImageDelete: PropTypes.func.isRequired,
  handleHighBarImageUpload: PropTypes.func.isRequired,
  handleHighBarImageDelete: PropTypes.func.isRequired,
  handleRightBarImageUpload: PropTypes.func.isRequired,
  handleRightBarImageDelete: PropTypes.func.isRequired,
  gameImageProgress: PropTypes.number,
  logoImageProgress: PropTypes.number,
  firstTeamImageProgress: PropTypes.number,
  secondTeamImageProgress: PropTypes.number,
  backgroundMenuProgress: PropTypes.number,
  backgroundSelectTeamProgress: PropTypes.number,
  backgroundImageProgress: PropTypes.number,
  backgroundNextLevelProgress: PropTypes.number,
  missImageProgress: PropTypes.number,
  outImageProgress: PropTypes.number,
  gameOverImageProgress: PropTypes.number,
  goalImageProgress: PropTypes.number,
  highBarImageProgress: PropTypes.number,
  rightBarImageProgress: PropTypes.number,
  logoImageValidationError: PropTypes.string,
  gameImageValidationError: PropTypes.string,
  secondTeamImageValidationError: PropTypes.string,
  teamImageValidationError: PropTypes.string,
  backgroundMenuValidationError: PropTypes.string,
  backgroundSelectTeamValidationError: PropTypes.string,
  backgroundImageValidationError: PropTypes.string,
  backgroundNextLevelValidationError: PropTypes.string,
  missImageValidationError: PropTypes.string,
  outImageValidationError: PropTypes.string,
  gameOverImageValidationError: PropTypes.string,
  goalImageValidationError: PropTypes.string,
  highBarImageValidationError: PropTypes.string,
  rightBarImageValidationError: PropTypes.string,
  handleGameImageFileError: PropTypes.func,
  handleLogoImageFileError: PropTypes.func,
  handleFirstTeamImageFileError: PropTypes.func,
  handleSecondTeamImageFileError: PropTypes.func,
  handleBackgroundMenuFileError: PropTypes.func,
  handleBackgroundSelectTeamFileError: PropTypes.func,
  handleBackgroundImageFileError: PropTypes.func,
  handleBackgroundNextLevelFileError: PropTypes.func,
  handleMissImageFileError: PropTypes.func,
  handleOutImageFileError: PropTypes.func,
  handleGameOverImageFileError: PropTypes.func,
  handleGoalImageFileError: PropTypes.func,
  handleHighBarImageFileError: PropTypes.func,
  handleRightBarImageFileError: PropTypes.func,
  showGuidelines: PropTypes.bool.isRequired,
  setShowGuidelines: PropTypes.func.isRequired
};

export default React.memo(FreeKickGameDisplayForm);
